import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { Eye, Pencil, Plus, Search, Trash2, Users } from 'lucide-react';
import api from '../../../../api/client';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import PageHeader, { PageBody, PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import CandidateGroupFormModal from './CandidateGroupFormModal';
import {
  apiErrorMessage,
  formatGroupDate,
  readStoredUser,
} from './candidateGroupUtils';

const hoverTipSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  },
  tooltip: {
    sx: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
      borderRadius: '8px',
      px: 1.25,
      py: 0.6,
      boxShadow: '0 8px 20px rgba(14, 165, 233, 0.28)',
      maxWidth: 280,
    },
  },
};

const ActionButton = ({ label, onClick, className, children }) => (
  <Tooltip title={label} placement="top" enterDelay={200} slotProps={hoverTipSlotProps}>
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${className}`}
    >
      {children}
    </button>
  </Tooltip>
);

export default function CandidateGroups() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userDetails = useMemo(() => readStoredUser(), []);
  const isSuperAdmin = userDetails?.is_superuser === true;
  const adminCompanies = Array.isArray(userDetails?.admin_companies) ? userDetails.admin_companies : [];
  const isCompanyAdmin = adminCompanies.length > 0;
  const companyRequired = isSuperAdmin || adminCompanies.length > 1;

  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(true);
  usePageLoading(loading, {
    message: t('candidateGroups.loading', 'Loading candidate groups…'),
  });
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState('-created_at');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_count: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formOpen, setFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (!isSuperAdmin && !isCompanyAdmin) {
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isCompanyAdmin, navigate]);

  useEffect(() => {
    if (!companyRequired) return;
    api.get('companies/')
      .then((res) => {
        const companiesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
        const visible = isSuperAdmin
          ? companiesData
          : companiesData.filter((company) => adminCompanies.includes(company.name));
        setCompanies(visible);
      })
      .catch(() => setCompanies([]));
  }, [companyRequired, isSuperAdmin, adminCompanies.join('|')]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, ordering };
      if (search.trim()) params.search = search.trim();
      if (selectedCompany) params.company = selectedCompany;
      const response = await api.get('candidate-groups/', { params });
      const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const count = response.data?.count ?? results.length;
      setGroups(results);
      setPagination({
        current_page: page,
        total_pages: Math.max(1, Math.ceil(count / pageSize)),
        total_count: count,
      });
    } catch (error) {
      setGroups([]);
      showSnackbar(apiErrorMessage(error, t('candidateGroups.loadFailed', 'Failed to load candidate groups.')), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering, selectedCompany, t]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSave = async (payload) => {
    if (!editingGroup && companyRequired && !selectedCompany) {
      showSnackbar(t('candidateGroups.companyRequired', 'Select a company before creating a group.'), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = { ...payload };
      if (!editingGroup && selectedCompany) body.company = Number(selectedCompany);
      if (editingGroup) {
        await api.patch(`candidate-groups/${editingGroup.id}/`, body);
        showSnackbar(t('candidateGroups.updated', 'Candidate group updated.'));
      } else {
        await api.post('candidate-groups/', body);
        showSnackbar(t('candidateGroups.created', 'Candidate group created.'));
      }
      setFormOpen(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      showSnackbar(apiErrorMessage(error, t('candidateGroups.saveFailed', 'Failed to save candidate group.')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`candidate-groups/${deleteTarget.id}/`);
      showSnackbar(t('candidateGroups.deleted', 'Candidate group deleted. Candidates and the company were not changed.'));
      setDeleteTarget(null);
      fetchGroups();
    } catch (error) {
      showSnackbar(apiErrorMessage(error, t('candidateGroups.deleteFailed', 'Failed to delete candidate group.')), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = Boolean(search.trim() || selectedCompany);

  return (
    <PageShell className="bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <PageCard>
          <PageHeader
            title={t('candidateGroups.title', 'Candidate Groups')}
            subtitle={t('candidateGroups.subtitle', 'Organize candidates without changing their profiles or access')}
          >
            <PageHeaderButton
              onClick={() => { setEditingGroup(null); setFormOpen(true); }}
              icon={<Plus size={14} />}
            >
              {t('candidateGroups.create', 'Create Candidate Group')}
            </PageHeaderButton>
          </PageHeader>

          <div className="grid grid-cols-1 gap-3 border-b border-gray-100 p-4 sm:grid-cols-2 lg:grid-cols-3 shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full rounded-xl border-2 border-gray-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('candidateGroups.searchPlaceholder', 'Search by group name')}
                value={search}
                onChange={(event) => { setPage(1); setSearch(event.target.value); }}
              />
            </div>
            <select
              className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={ordering}
              onChange={(event) => { setPage(1); setOrdering(event.target.value); }}
            >
              <option value="-created_at">{t('candidateGroups.sortNewest', 'Newest first')}</option>
              <option value="name">{t('candidateGroups.sortName', 'Name')}</option>
              <option value="-candidate_count">{t('candidateGroups.sortCount', 'Most candidates')}</option>
              <option value="-updated_at">{t('candidateGroups.sortUpdated', 'Recently updated')}</option>
            </select>
            {companyRequired && (
              <select
                className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedCompany}
                onChange={(event) => { setPage(1); setSelectedCompany(event.target.value); }}
              >
                <option value="">{t('jobDescription.allCompanies', 'All companies')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            )}
          </div>

          <PageBody className="p-4 sm:p-6">
            {loading ? null : groups.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                <Users size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-base font-medium text-gray-800">
                  {hasFilters
                    ? t('candidateGroups.noResults', 'No groups match your search.')
                    : t('candidateGroups.empty', 'No candidate groups yet.')}
                </p>
                {!hasFilters && (
                  <button
                    onClick={() => { setEditingGroup(null); setFormOpen(true); }}
                    className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {t('candidateGroups.createFirst', 'Create your first group')}
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[960px] text-sm">
                  <thead className="bg-gray-50/80 text-left text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-3">{t('candidateGroups.name', 'Group Name')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.description', 'Description')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.totalCandidates', 'Total Candidates')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.createdBy', 'Created By')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.createdDate', 'Created Date')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.updatedBy', 'Last Updated By')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.updatedDate', 'Last Updated Date')}</th>
                      <th className="px-4 py-3 text-center">{t('candidate.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groups.map((group) => (
                      <tr
                        key={group.id}
                        className="cursor-pointer hover:bg-sky-50/50"
                        onClick={() => navigate(`/candidate-groups/${group.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <span className="inline-flex items-center gap-2.5">
                            <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-base leading-none" aria-hidden="true">
                              {group.logo || '•'}
                            </span>
                            {group.name}
                          </span>
                        </td>
                        <td className="max-w-[220px] truncate px-4 py-3 text-gray-600" title={group.description || ''}>
                          {group.description || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
                            {group.candidate_count ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{group.created_by_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{formatGroupDate(group.created_at)}</td>
                        <td className="px-4 py-3 text-gray-700">{group.updated_by_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{formatGroupDate(group.updated_at)}</td>
                        <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1.5">
                            <ActionButton
                              label={t('candidateGroups.view', 'View')}
                              onClick={() => navigate(`/candidate-groups/${group.id}`)}
                              className="border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100"
                            >
                              <Eye size={15} />
                            </ActionButton>
                            <ActionButton
                              label={t('candidateGroups.edit', 'Edit')}
                              onClick={() => { setEditingGroup(group); setFormOpen(true); }}
                              className="border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                            >
                              <Pencil size={15} />
                            </ActionButton>
                            <ActionButton
                              label={t('candidateGroups.manage', 'Manage Candidates')}
                              onClick={() => navigate(`/candidate-groups/${group.id}`)}
                              className="border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                            >
                              <Users size={15} />
                            </ActionButton>
                            <ActionButton
                              label={t('candidateGroups.delete', 'Delete')}
                              onClick={() => setDeleteTarget(group)}
                              className="border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                            >
                              <Trash2 size={15} />
                            </ActionButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              totalCount={pagination.total_count}
              currentCount={groups.length}
              onPageChange={setPage}
            />
          </PageBody>
      </PageCard>

      <CandidateGroupFormModal
        open={formOpen}
        group={editingGroup}
        saving={saving}
        companies={companies}
        companyId={selectedCompany}
        companyRequired={companyRequired && !editingGroup}
        onCompanyChange={setSelectedCompany}
        onClose={() => { setFormOpen(false); setEditingGroup(null); }}
        onSave={handleSave}
      />
      <ConfirmationDialog
        open={Boolean(deleteTarget) && !deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('candidateGroups.deleteTitle', 'Delete candidate group')}
        message={t(
          'candidateGroups.deleteMessage',
          'Delete this group? Candidates stay in the company, and their other groups, interviews, and profiles are not affected.'
        )}
        confirmText={t('candidateGroups.delete', 'Delete')}
      />
    </PageShell>
  );
}
